import * as React from 'react';

import type {History} from 'history';

import {Button} from 'shells/button/button';
import {colors} from 'shells/colors/colors';
import {CheckIcon, WarningIcon} from 'shells/icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import type {MessageType} from 'shells/message/message';

import type {
    CalendarSync,
    GetCalendars_calendars as ConflictCalendar,
} from 'nutshell-graphql-types';

import {AllConnectedCalendarsInfo} from './all-connected-calendars-info';
import {EnableCalendar} from './enable-calendar';

import styles from './calendar-status.module.css';

const windowFeatures = {
    toolbar: 'no',
    location: 'no',
    width: 600,
    height: 850,
} as const;

export function getFlashBannerType(
    status?: CalendarSync | null,
    errorMessage?: string | null,
    conflictSyncError?: string | null
): MessageType {
    if (errorMessage) {
        return 'error';
    } else if (!status) {
        return 'info';
    } else if (!status.isEnabled || conflictSyncError) {
        return 'warning';
    } else {
        return 'success';
    }
}

export function getStatusIcon(
    status?: CalendarSync | null,
    errorMessage?: string | null,
    conflictSyncError?: string | null
): React.ReactElement<any> {
    let icon;
    let color;

    if (errorMessage) {
        // @ts-expect-error
        icon = <WarningIcon color={colors.rose} size={15} />;
    } else if (status) {
        if (status.isEnabled && !conflictSyncError) {
            icon = <CheckIcon />;
            color = colors.green;
        } else {
            // @ts-expect-error
            icon = <WarningIcon color={colors.orangeAttention} size={15} />;
        }
    }

    return (
        <div style={{color}} className={styles['status-icon']}>
            {icon}
        </div>
    );
}

export function getStatusText(
    status?: CalendarSync | null,
    errorMessage?: string | null,
    conflictSyncError?: string | null
): string {
    if (errorMessage) {
        return 'Calendar: Connection problems';
    } else if (status) {
        if (status.isEnabled && !conflictSyncError) {
            return 'Calendar: Connected';
        } else {
            return 'Calendar: Needs attention';
        }
    } else {
        return 'Calendar: Not yet connected';
    }
}

export function getStatusDescription(
    status?: CalendarSync | null,
    errorMessage?: string | null,
    conflictSyncError?: string | null
): React.ReactElement<any> {
    const externalServiceName =
        status && status.externalService === 'OFFICE365' ? 'Office 365' : 'Google';

    if (errorMessage) {
        return <div>Your calendar is having connection problems</div>;
    } else if (status) {
        if (status.isEnabled && !conflictSyncError) {
            return (
                <div>
                    <div>Your {externalServiceName} Calendar is connected</div>
                </div>
            );
        } else if (status.isEnabled && conflictSyncError) {
            return <div>Your calendars need attention</div>;
        } else {
            return (
                <div>
                    <div>Your calendar is connected but not enabled</div>
                </div>
            );
        }
    }

    return (
        <div>
            <div>Your calendar is not yet connected</div>
        </div>
    );
}

export function getPopoverContent(input: {
    status: CalendarSync | null | undefined;
    conflictCalendars: ConflictCalendar[];
    refetchSyncStatus: () => Promise<any>;
    errorMessage: string | null | undefined;
    setGoogleCalendarSyncIsEnabled: (isEnabled: boolean) => Promise<any>;
    setOffice365CalendarSyncIsEnabled: (isEnabled: boolean) => Promise<any>;
    setIsOnboardingModalOpen: (isOnboardingModalOpen: boolean) => void;
    setIsEditModalOpen: (isEditOnboardingModalOpen: boolean) => void;
    setIsReconnectOnboardingModalOpen: (isReconnectOnboardingModalOpen: boolean) => void;
    setIsDialogOpen: (isDialogOpen: boolean) => void;
    onAuthError?: (errMsg: string) => void;
    onAuthStart?: () => void;
    appHistory: History;
}): React.ReactElement<any> {
    const {
        status,
        refetchSyncStatus,
        errorMessage,
        setGoogleCalendarSyncIsEnabled,
        setOffice365CalendarSyncIsEnabled,
        setIsOnboardingModalOpen,
        setIsEditModalOpen,
        setIsReconnectOnboardingModalOpen,
        setIsDialogOpen,
        onAuthError,
        onAuthStart,
        appHistory,
    } = input;

    if (errorMessage) {
        return (
            <div className={styles['body-container']}>
                <StackLegacy spacing={16} alignCenter={true}>
                    Your meetings cannot be booked until you reconnect your calendar.
                    <Button
                        variant='primary'
                        size='big'
                        onClick={() => {
                            setIsReconnectOnboardingModalOpen(true);
                            setIsDialogOpen(false);
                        }}
                    >
                        Reconnect
                    </Button>
                </StackLegacy>
            </div>
        );
    } else if (status) {
        if (status.isEnabled) {
            return (
                <AllConnectedCalendarsInfo
                    status={status}
                    setIsOnboardingModalOpen={setIsEditModalOpen}
                    setIsDialogOpen={setIsDialogOpen}
                    conflictCalendars={input.conflictCalendars}
                />
            );
        } else {
            return (
                <div className={styles['body-container']}>
                    <EnableCalendar
                        status={status}
                        windowFeatures={windowFeatures}
                        onAuthError={onAuthError}
                        onAuthStart={onAuthStart}
                        refetchSyncStatus={refetchSyncStatus}
                        setOffice365CalendarSyncIsEnabled={setOffice365CalendarSyncIsEnabled}
                        setGoogleCalendarSyncIsEnabled={setGoogleCalendarSyncIsEnabled}
                    />
                </div>
            );
        }
    }

    return (
        <div className={styles['body-container']}>
            <StackLegacy spacing={16} alignCenter={true}>
                To start allowing people to book meetings with you, connect your Google or Office365
                calendar.
                <Button
                    variant='primary'
                    size='big'
                    onClick={() => {
                        if (NutClientConfig.hasImprovedEmailCalendarSyncFF) {
                            appHistory.replace({
                                hash: '/connect-calendar',
                            });
                        } else {
                            setIsOnboardingModalOpen(true);
                        }
                        setIsDialogOpen(false);
                    }}
                >
                    Get started
                </Button>
            </StackLegacy>
        </div>
    );
}
